import { useEffect, useState } from 'react'

export type LoadingCaption = {
    caption: string
    durationMSs?: number
}

interface Props {
    show?: boolean
    fullScreen?: boolean
    captions?: LoadingCaption[]
    className?: string
    spinnerSize?: number | string // New prop for dynamic spinner size
    // New props for customizing colours:
    outerCircleClass?: string // For the outer circle’s border color
    iconClass?: string // For optional custom styling of the inner logo
    showSpinner?: boolean // NEW: optionally hide spinner (still show captions). Defaults to true for backward compatibility
}

function LoadingSpinner({
    show = true,
    fullScreen = false,
    captions = [],
    spinnerSize = '40px',
    className,
    outerCircleClass, // optional class to override default outer border color
    iconClass, // optional class to tweak the logo styling
    showSpinner = true, // default true for backward compatibility
}: Props): React.JSX.Element {
    const [captionIndex, setCaptionIndex] = useState(0)

    useEffect(() => {
        if (!captions.length || captionIndex >= captions.length - 1) return

        const currentCaption = captions[captionIndex]
        const duration = currentCaption.durationMSs ?? 3000 // Default to 3s if no duration is provided

        const timer = setTimeout(() => {
            setCaptionIndex((prevIndex) => prevIndex + 1)
        }, duration)

        return () => clearTimeout(timer) // Cleanup on unmount or update
    }, [captionIndex, captions])

    if (!show) {
        return <></>
    }

    if (fullScreen) {
        return (
            <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-gray-500 bg-opacity-20 pointer-events-auto">
                <LoadingSpinner
                    show={true}
                    fullScreen={false}
                    captions={captions}
                    spinnerSize={spinnerSize}
                    className={className}
                    outerCircleClass={outerCircleClass}
                    iconClass={iconClass}
                    showSpinner={showSpinner}
                />
            </div>
        )
    }

    // Convert spinnerSize to a valid CSS size string if a number is provided.
    const size =
        typeof spinnerSize === 'number' ? `${spinnerSize}px` : spinnerSize
    // Determine a numeric value for the spinner size (assumes px) to calculate logo size.
    const numericSize =
        typeof spinnerSize === 'number'
            ? spinnerSize
            : parseInt(spinnerSize) || 40
    // Set the logo size to 60% of the spinner's size.
    const logoSize = numericSize * 0.6

    // Default class (colour) for the outer circle if not provided.
    const defaultOuterColor = 'border-ctxt-blue'

    const containerSpacing =
        showSpinner && captions.length > 0 ? 'space-y-4' : ''

    return (
        <div
            className={`flex flex-col justify-center items-center h-full w-full ${containerSpacing} ${
                className ?? ''
            }`}
        >
            {/* Spinner (optional) */}
            {showSpinner && (
                <div className="relative">
                    <div
                        className={`animate-spin rounded-full border-t-2 border-opacity-75 ${
                            outerCircleClass
                                ? outerCircleClass
                                : defaultOuterColor
                        }`}
                        style={{ height: size, width: size }}
                    ></div>
                    <img
                        src="/Contxt_Marque_ROUNDED.png"
                        alt="Contxt logo"
                        style={{
                            height: `${logoSize}px`,
                            width: `${logoSize}px`,
                        }}
                        className={`absolute inset-0 m-auto p-1 rounded-lg opacity-75 ${
                            iconClass ? iconClass : ''
                        }`}
                    />
                </div>
            )}

            {/* Caption */}
            {captions.length > 0 && (
                <p
                    className="text-lg text-center 
                               inline-block 
                               bg-narrow-spotlight
                               bg-[length:700px_100%] 
                               animate-shimmer 
                               text-transparent 
                               bg-clip-text"
                >
                    {captions[captionIndex].caption}
                </p>
            )}
        </div>
    )
}

export default LoadingSpinner
